import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { TargetComparison } from '../../../../../api/targets-client/targets-client.type';
import type { MeasureValuesOverTime } from '../../../../../api/work-periods-client/work-periods-client.type';
import { formatDate, formatDateRange } from '../../../../../helpers/date-time-helpers';
import { simpleLinearRegression } from '../../../../../helpers/math-helpers/math-helpers';
import type { MetricsResult } from './side-section.type';

/**
 * Formats a numeric value for display, handling null values and decimal places
 *
 * @param {number | null} value - The numeric value to format
 * @returns {string} The formatted value as a string:
 *   - Returns '-' for null values
 *   - Returns whole numbers without decimal places
 *   - Returns numbers with decimals fixed to 1 decimal place
 */
const formatValue = (value: number | null): string => {
  if (value === null) {
    return '-';
  }

  // If the value is a whole number, display without decimal places
  if (value === Math.floor(value)) {
    return value.toString();
  }

  return value.toFixed(1);
};

/**
 * Calculates the average value of the given monthly data.
 *
 * @param {MeasureValuesOverTime} data - The data to calculate the average from.
 * @param {boolean} isZeroValid - Whether zero values should be included in the calculation
 * @return {number | null} The average value of the data. Returns null if the data is empty or all values are null.
 */
const calculateAverage = (data: MeasureValuesOverTime, isZeroValid: boolean): number | null => {
  const isValid = (value: number | null): value is number => value !== null && (isZeroValid || value !== 0);
  const validValues = Object.values(data).filter(isValid);

  if (validValues.length === 0) {
    return null;
  }

  return validValues.reduce((acc, value) => acc + value, 0) / validValues.length;
};

/**
 * Sorts the given measure data by date in ascending order.
 *
 * @param {MeasureValuesOverTime} data - The measure data to be sorted.
 * @return {MeasureValuesOverTime} The sorted measure data.
 */
const sortMeasureDataOverTime = (data: MeasureValuesOverTime): MeasureValuesOverTime => {
  const sortedLabels = Object.keys(data).sort((a, b) => dayjs(a).diff(dayjs(b)));

  return sortedLabels.reduce((acc, label) => {
    acc[label] = data[label];
    return acc;
  }, {} as MeasureValuesOverTime);
};

/**
 * Calculates the trend of the measure values over time.
 * Uses simple linear regression to determine the trend direction and magnitude.
 *
 * @param {MeasureValuesOverTime} data - The measure data points over time
 * @param {boolean} isZeroValid - Whether zero values should be included in the calculation
 * @returns {number | null} - The trend slope, or null if insufficient data
 */
const calculateTrend = (data: MeasureValuesOverTime, isZeroValid: boolean): number | null => {
  const isValid = (value: number | null): value is number => value !== null && (isZeroValid || value !== 0);
  const validValues = Object.values(sortMeasureDataOverTime(data)).filter(isValid);

  if (validValues.length < 2) {
    return null;
  }

  const xAxis = Array.from({ length: validValues.length }, (_, index) => index);
  const line = simpleLinearRegression(xAxis, validValues);

  return line?.slope || null;
};

/**
 * Calculates the forecast for the next value based on the trend and the data.
 *
 * @param {MeasureValuesOverTime} data - The measure data points over time
 * @param {boolean} isZeroValid - Whether zero values should be included in the calculation
 * @returns {number | null} - The forecast for the next value, or null if trend calculation fails
 */
const calculateForecast = (data: MeasureValuesOverTime, isZeroValid: boolean): number | null => {
  const isValid = (value: number | null): value is number => value !== null && (isZeroValid || value !== 0);
  const validValues = Object.values(sortMeasureDataOverTime(data)).filter(isValid);

  if (validValues.length < 2) {
    return null;
  }

  const xAxis = Array.from({ length: validValues.length }, (_, index) => index);
  const trend = simpleLinearRegression(xAxis, validValues);

  if (!trend) {
    return null;
  }

  if (Object.keys(data).length === 0) {
    return trend.intercept;
  }

  return trend.slope * (Object.keys(data).length + 1) + trend.intercept;
};

/**
 * Calculates aggregated metrics for the given measure data.
 *
 * @param {MeasureValuesOverTime} data - The measure data over time
 * @param {boolean} isZeroValid - Whether zero values should be included in calculations
 * @returns {MetricsResult} Object containing average, trend slope, and forecast values
 */
const calculateMetrics = (data: MeasureValuesOverTime, isZeroValid: boolean): MetricsResult => {
  return {
    averageValue: calculateAverage(data, isZeroValid),
    trendSlope: calculateTrend(data, isZeroValid),
    forecast: calculateForecast(data, isZeroValid),
  };
};

/**
 * Formats a target comparison symbol for display
 *
 * @param {TargetComparison} comparison - The type of target comparison
 * @param {number} value - The target value
 * @param {number | null} upperValue - The upper target value for range comparisons
 * @returns {string} Formatted string representation of the comparison
 */
const getTargetComparisonSymbol = (comparison: TargetComparison, value: number, upperValue: number | null): string => {
  switch (comparison) {
    case TargetComparison.LT:
      return `<${value}`;
    case TargetComparison.LTE:
      return `≤${value}`;
    case TargetComparison.EQ:
      return `=${value}`;
    case TargetComparison.GTE:
      return `≥${value}`;
    case TargetComparison.GT:
      return `>${value}`;
    case TargetComparison.RANGE_IN:
      return `[${value}, ${upperValue}]`;
    case TargetComparison.RANGE_EX:
      return `(${value}, ${upperValue})`;
    default:
      return `=${value}`;
  }
};

/**
 * Formats a target date range for display
 *
 * @param {string} startDate - The start date string
 * @param {string | null} endDate - The end date string, or null for ongoing
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} Formatted date range string
 */
const formatTargetDateRange = (startDate: string, endDate: string | null, t: TFunction): string => {
  if (!endDate) {
    return `${formatDate(startDate)} – ${t('process_analysis.side_section.ongoing')}`;
  }

  return formatDateRange(startDate, endDate);
};

export { calculateMetrics, formatTargetDateRange, formatValue, getTargetComparisonSymbol };
